// =====================================================
// COMPONENTE: CupomInput - Campo para aplicar cupons
// =====================================================

import { useState } from 'react'
import { Tag, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { supabase } from '../services/supabase'
import { useToast } from '../hooks/useToast'

interface Cupom {
    id: string
    codigo: string
    nome: string
    tipo: 'desconto_percentual' | 'desconto_fixo' | 'trial_gratis' | 'preco_fixo'
    valor: number
    plano_aplicavel: string
    data_inicio: string
    data_fim: string
    limite_uso: number | null
    usado_count: number
    ativo: boolean
}

interface CupomAplicado {
    cupom: Cupom
    desconto: number
    valorFinal: number
}

interface CupomInputProps {
    planoSelecionado: string
    valorOriginal: number
    onCupomAplicado: (cupomAplicado: CupomAplicado | null) => void
    className?: string
}

export function CupomInput({
    planoSelecionado,
    valorOriginal,
    onCupomAplicado,
    className = ""
}: CupomInputProps) {
    const [codigoCupom, setCodigoCupom] = useState('')
    const [loading, setLoading] = useState(false)
    const [cupomAplicado, setCupomAplicado] = useState<CupomAplicado | null>(null)
    const [erro, setErro] = useState('')
    const { showSuccess, showError } = useToast()

    const validarCupom = async () => {
        if (!codigoCupom.trim()) {
            setErro('Digite um código de cupom')
            return
        }

        try {
            setLoading(true)
            setErro('')

            // Usar função SQL para validar cupom
            const { data, error } = await supabase
                .rpc('validar_cupom', {
                    p_codigo: codigoCupom.trim().toUpperCase(),
                    p_plano: planoSelecionado
                })

            if (error) {
                console.error('Erro ao validar cupom:', error)
                throw new Error('Erro ao validar cupom')
            }

            if (!data?.valido) {
                setErro(data?.erro || 'Cupom inválido')
                onCupomAplicado(null)
                setCupomAplicado(null)
                return
            }

            const cupom = data.cupom
            const desconto = calcularDesconto(cupom, valorOriginal)
            const valorFinal = valorOriginal - desconto

            const cupomAplicadoData: CupomAplicado = {
                cupom,
                desconto,
                valorFinal
            }

            setCupomAplicado(cupomAplicadoData)
            onCupomAplicado(cupomAplicadoData)
            showSuccess('Cupom aplicado com sucesso!')

        } catch (err) {
            console.error('❌ Erro ao validar cupom:', err)

            let errorMessage = 'Erro ao validar cupom'
            if (err instanceof Error) {
                errorMessage = err.message
            } else if (typeof err === 'object' && err !== null) {
                const supabaseError = err as any
                if (supabaseError.message) {
                    errorMessage = supabaseError.message
                } else if (supabaseError.details) {
                    errorMessage = supabaseError.details
                }
            }

            console.error('❌ Detalhes do erro:', err)
            setErro(errorMessage)
            showError(errorMessage)
            onCupomAplicado(null)
            setCupomAplicado(null)
        } finally {
            setLoading(false)
        }
    }

    const calcularDesconto = (cupom: Cupom, valor: number): number => {
        switch (cupom.tipo) {
            case 'desconto_percentual':
                return (valor * cupom.valor) / 100
            case 'desconto_fixo':
                return Math.min(cupom.valor, valor) // Não pode ser maior que o valor original
            case 'trial_gratis':
                return valor // 100% de desconto para trial
            case 'preco_fixo':
                return Math.max(0, valor - cupom.valor) // Diferença entre valor original e preço fixo
            default:
                return 0
        }
    }

    const formatarDesconto = (cupom: Cupom) => {
        switch (cupom.tipo) {
            case 'desconto_percentual':
                return `${cupom.valor}% de desconto`
            case 'desconto_fixo':
                return `R$ ${cupom.valor.toFixed(2)} de desconto`
            case 'trial_gratis':
                return `${cupom.valor} dias grátis`
            case 'preco_fixo':
                return `R$ ${cupom.valor.toFixed(2)}`
            default:
                return 'Desconto aplicado'
        }
    }

    const removerCupom = () => {
        setCodigoCupom('')
        setCupomAplicado(null)
        setErro('')
        onCupomAplicado(null)
    }

    return (
        <div className={`space-y-3 ${className}`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Código do Cupom
            </label>

            <div className="flex gap-2">
                <div className="flex-1 relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={codigoCupom}
                        onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                        placeholder="Digite o código do cupom"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                        disabled={loading || !!cupomAplicado}
                    />
                </div>

                {!cupomAplicado ? (
                    <button
                        onClick={validarCupom}
                        disabled={loading || !codigoCupom.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Tag className="w-4 h-4" />
                        )}
                        Aplicar
                    </button>
                ) : (
                    <button
                        onClick={removerCupom}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <XCircle className="w-4 h-4" />
                        Remover
                    </button>
                )}
            </div>

            {/* Status do Cupom */}
            {erro && (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>{erro}</span>
                </div>
            )}

            {cupomAplicado && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium">Cupom Aplicado</span>
                    </div>
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Cupom:</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                                {cupomAplicado.cupom.nome}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Benefício:</span>
                            <span className="font-medium text-green-600 dark:text-green-400">
                                {formatarDesconto(cupomAplicado.cupom)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
                            <span className="font-medium text-green-600 dark:text-green-400">
                                -R$ {cupomAplicado.desconto.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between border-t border-green-200 dark:border-green-700 pt-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">Valor Final:</span>
                            <span className="font-bold text-green-600 dark:text-green-400">
                                R$ {cupomAplicado.valorFinal.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
